<template>
  <Teleport to="body">
    <!-- Active call widget -->
    <Transition name="call-widget">
      <div v-if="isCallActive" class="acw" :class="stateClass">
        <!-- Top bar with status -->
        <div class="acw__topbar">
          <div class="acw__indicator" />
          <span class="acw__status-text">{{ statusLabel }}</span>
          <span v-if="store.callIsGeneral" class="acw__general-badge">
            {{ t('telephony.activeCall.generalCall') }}
          </span>
          <span v-if="store.callState === 'answered'" class="acw__timer">{{ formattedTime }}</span>
        </div>

        <!-- Contact info -->
        <div class="acw__contact">
          <div class="acw__avatar" :class="avatarClass">
            <span class="acw__avatar-text">{{ avatarInitials }}</span>
          </div>
          <div class="acw__contact-details">
            <span v-if="displayName" class="acw__name">{{ displayName }}</span>
            <span class="acw__phone" :class="{ 'acw__phone--primary': !displayName }">
              {{ displayNumber }}
            </span>
          </div>
        </div>

        <!-- Ringing animation -->
        <div v-if="store.callState === 'ringing'" class="acw__ringing">
          <div class="acw__ring-dots">
            <span class="acw__ring-dot" />
            <span class="acw__ring-dot" />
            <span class="acw__ring-dot" />
          </div>
          <span class="acw__ring-label">{{ t('telephony.activeCall.callingEllipsis') }}</span>
        </div>

        <!-- Ended state -->
        <div v-if="store.callState === 'ended'" class="acw__ended">
          <span v-if="store.callDisposition" class="acw__disposition">
            {{ dispositionLabel }}
          </span>
          <span v-if="store.callDuration" class="acw__final-time">
            {{ formatDuration(store.callDuration) }}
          </span>
        </div>

        <!-- Action buttons -->
        <div v-if="store.callState !== 'ended'" class="acw__actions">
          <button
            class="acw__btn acw__btn--mute"
            :class="{ 'acw__btn--active': isMuted }"
            :title="t('telephony.activeCall.microphone')"
            @click="isMuted = !isMuted"
          >
            <MicOffIcon v-if="isMuted" :size="18" />
            <MicIcon v-else :size="18" />
          </button>

          <button
            class="acw__btn acw__btn--hangup"
            :title="t('telephony.activeCall.endCall')"
            @click="handleEndCall"
          >
            <PhoneOffIcon :size="20" />
          </button>
        </div>
      </div>
    </Transition>
    <!-- DEV-425: idle-индикатор статуса SIP перенесён в топбар (SipStatusIndicator). -->
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { PhoneOffIcon, MicIcon, MicOffIcon } from 'lucide-vue-next'
import { useTelephonyStore } from '@/features/telephony/stores/telephony.store'
import { useSipClient } from '@/features/telephony/composables/useSipClient'

const { t } = useI18n()
const store = useTelephonyStore()
const sipClient = useSipClient()

const isMuted = ref(false)
const elapsedSeconds = ref(0)
let timerInterval: ReturnType<typeof setInterval> | null = null
let ringbackAudio: HTMLAudioElement | null = null

const isCallActive = computed(() => store.isCallActive)

const stateClass = computed(() => ({
  'acw--ringing': store.callState === 'ringing',
  'acw--answered': store.callState === 'answered',
  'acw--ended': store.callState === 'ended',
}))

const avatarClass = computed(() => ({
  'acw__avatar--ringing': store.callState === 'ringing',
  'acw__avatar--active': store.callState === 'answered',
  'acw__avatar--ended': store.callState === 'ended',
}))

const displayName = computed(() => {
  return store.activeCallClientName || null
})

const displayNumber = computed(() => {
  return store.callPhoneNumber || t('telephony.call.calling')
})

const avatarInitials = computed(() => {
  if (store.activeCallClientName) {
    const parts = store.activeCallClientName.trim().split(/\s+/)
    const [first = '', second = ''] = parts
    if (first && second) {
      return (first.charAt(0) + second.charAt(0)).toUpperCase()
    }
    return first.substring(0, 2).toUpperCase()
  }
  return displayNumber.value.slice(-2)
})

const statusLabel = computed(() => {
  switch (store.callState) {
    case 'ringing':
      return t('telephony.activeCall.ringing')
    case 'answered':
      return t('telephony.activeCall.answered')
    case 'ended':
      return t('telephony.activeCall.ended')
    default:
      return t('telephony.activeCall.call')
  }
})

const dispositionLabel = computed(() => {
  const map: Record<string, string> = {
    answered: t('telephony.disposition.answered'),
    busy: t('telephony.disposition.busy'),
    cancel: t('telephony.disposition.cancel'),
    'no answer': t('telephony.disposition.noAnswer'),
    failed: t('telephony.disposition.failed'),
  }
  return map[store.callDisposition] || store.callDisposition
})

const formattedTime = computed(() => {
  const m = Math.floor(elapsedSeconds.value / 60)
  const s = elapsedSeconds.value % 60
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
})

const formatDuration = (seconds: number): string => {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
}

const startTimer = () => {
  stopTimer()
  elapsedSeconds.value = 0
  timerInterval = setInterval(() => {
    elapsedSeconds.value++
  }, 1000)
}

const stopTimer = () => {
  if (timerInterval) {
    clearInterval(timerInterval)
    timerInterval = null
  }
}

/**
 * Ringback tone — генерация стандартного гудка через Web Audio API.
 * Частоты 440Hz + 480Hz (US ringback), 2s on / 4s off.
 */
function startRingbackTone() {
  stopRingbackTone()
  try {
    const ctx = new AudioContext()
    const gainNode = ctx.createGain()
    gainNode.gain.value = 0.08
    gainNode.connect(ctx.destination)

    const osc1 = ctx.createOscillator()
    osc1.frequency.value = 440
    osc1.type = 'sine'
    osc1.connect(gainNode)

    const osc2 = ctx.createOscillator()
    osc2.frequency.value = 480
    osc2.type = 'sine'
    osc2.connect(gainNode)

    osc1.start()
    osc2.start()

    // 2s on / 4s off cycle via gain scheduling
    const scheduleCycle = () => {
      const now = ctx.currentTime
      for (let i = 0; i < 20; i++) {
        const cycleStart = now + i * 6
        gainNode.gain.setValueAtTime(0.08, cycleStart)
        gainNode.gain.setValueAtTime(0, cycleStart + 2)
      }
    }
    scheduleCycle()

    // Store references for cleanup
    ringbackAudio = {
      pause: () => {
        osc1.stop()
        osc2.stop()
        ctx.close()
      },
    } as unknown as HTMLAudioElement
  } catch {
    // Web Audio not supported — silent fallback
  }
}

function stopRingbackTone() {
  if (ringbackAudio) {
    try {
      ringbackAudio.pause()
    } catch {
      // ignore
    }
    ringbackAudio = null
  }
}

const handleEndCall = () => {
  sipClient.hangup()
  store.clearActiveCall()
}

watch(
  () => store.callState,
  (state, prevState) => {
    if (state === 'ringing' && prevState !== 'ringing') {
      startRingbackTone()
    } else if (state !== 'ringing') {
      stopRingbackTone()
    }

    if (state === 'answered') {
      isMuted.value = false
      startTimer()
    } else if (state === 'ended' || state === 'idle') {
      stopTimer()
    }
  },
)

onMounted(() => {
  sipClient.connect().catch((err: unknown) => {
    console.error('[SIP] Init failed in ActiveCallWidget:', err)
  })
})

onBeforeUnmount(() => {
  stopTimer()
  stopRingbackTone()
  if (!store.isCallActive) {
    sipClient.disconnect()
  }
})
</script>

<style scoped>
/* === Active Call Widget === */
.acw {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px 20px;
  min-width: 260px;
  max-width: 300px;
  background: linear-gradient(145deg, rgba(24, 24, 27, 0.97), rgba(30, 30, 36, 0.97));
  color: #fff;
  border-radius: 20px;
  box-shadow:
    0 12px 40px rgba(0, 0, 0, 0.35),
    0 0 0 1px rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
}

/* Top bar */
.acw__topbar {
  display: flex;
  align-items: center;
  gap: 8px;
}

.acw__indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 8px rgba(16, 185, 129, 0.5);
  animation: pulse-glow 2s ease-in-out infinite;
}

.acw--ringing .acw__indicator {
  background: #f59e0b;
  box-shadow: 0 0 8px rgba(245, 158, 11, 0.5);
}

.acw--ended .acw__indicator {
  background: #6b7280;
  box-shadow: none;
  animation: none;
}

.acw__status-text {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #10b981;
  flex: 1;
}

.acw--ringing .acw__status-text {
  color: #f59e0b;
}

.acw__general-badge {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #fff;
  background: rgba(59, 130, 246, 0.85);
  padding: 2px 6px;
  border-radius: 6px;
  white-space: nowrap;
}

.acw--ended .acw__status-text {
  color: #6b7280;
}

.acw__timer {
  font-size: 14px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: rgba(255, 255, 255, 0.7);
  font-family: 'SF Mono', 'Fira Code', monospace;
}

/* Contact */
.acw__contact {
  display: flex;
  align-items: center;
  gap: 12px;
}

.acw__avatar {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.acw__avatar--ringing {
  background: rgba(245, 158, 11, 0.2);
  animation: avatar-ring 1.5s ease-in-out infinite;
}

.acw__avatar--active {
  background: rgba(16, 185, 129, 0.2);
}

.acw__avatar--ended {
  background: rgba(107, 114, 128, 0.2);
}

.acw__avatar-text {
  font-size: 15px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.9);
  letter-spacing: 0.5px;
}

.acw__contact-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.acw__name {
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.acw__phone {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  font-variant-numeric: tabular-nums;
}

.acw__phone--primary {
  font-size: 15px;
  font-weight: 600;
  color: #fff;
}

/* Ringing dots animation */
.acw__ringing {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 4px 0;
}

.acw__ring-dots {
  display: flex;
  gap: 4px;
}

.acw__ring-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #f59e0b;
  animation: ring-bounce 1.4s ease-in-out infinite;
}

.acw__ring-dot:nth-child(2) {
  animation-delay: 0.2s;
}

.acw__ring-dot:nth-child(3) {
  animation-delay: 0.4s;
}

.acw__ring-label {
  font-size: 13px;
  color: rgba(245, 158, 11, 0.8);
  font-weight: 500;
}

/* Ended */
.acw__ended {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 4px 0;
}

.acw__disposition {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
}

.acw__final-time {
  font-size: 14px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: rgba(255, 255, 255, 0.8);
  font-family: 'SF Mono', 'Fira Code', monospace;
}

/* Actions */
.acw__actions {
  display: flex;
  justify-content: center;
  gap: 16px;
  padding-top: 2px;
}

.acw__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.acw__btn--mute {
  width: 42px;
  height: 42px;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.8);
}

.acw__btn--mute:hover {
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
}

.acw__btn--active {
  background: #f59e0b;
  color: #18181b;
}

.acw__btn--active:hover {
  background: #eab308;
}

.acw__btn--hangup {
  width: 48px;
  height: 48px;
  background: #ef4444;
  color: #fff;
  box-shadow: 0 4px 14px rgba(239, 68, 68, 0.4);
}

.acw__btn--hangup:hover {
  background: #dc2626;
  transform: scale(1.08);
  box-shadow: 0 6px 20px rgba(239, 68, 68, 0.5);
}

/* Transitions */
.call-widget-enter-active,
.call-widget-leave-active {
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.call-widget-enter-from {
  opacity: 0;
  transform: translateY(24px) scale(0.92);
}

.call-widget-leave-to {
  opacity: 0;
  transform: translateY(24px) scale(0.92);
}

/* Animations */
@keyframes pulse-glow {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

@keyframes ring-bounce {
  0%,
  80%,
  100% {
    transform: scale(0.6);
    opacity: 0.4;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes avatar-ring {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.3);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(245, 158, 11, 0);
  }
}
</style>
